import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";

import { App } from "./App";
import { ApiError } from "./api/client";
import "./index.css";
import { AuthProvider } from "./lib/auth";

/**
 * A 401 from any data request means the session expired (12h sliding / 7d absolute). Refetching
 * `/api/auth/me` turns that into a signed-out state, and RequireAuth sends the user to /login
 * with ?next= so they come back to the same page.
 */
function onUnauthorized(err: unknown, queryKey?: readonly unknown[]) {
  if (!(err instanceof ApiError) || err.status !== 401) return;
  if (queryKey && queryKey[0] === "auth") return;
  void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (err, query) => onUnauthorized(err, query.queryKey) }),
  mutationCache: new MutationCache({ onError: (err) => onUnauthorized(err) }),
  defaultOptions: {
    queries: {
      // 4xx won't fix itself on retry; only retry transient failures.
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
